import type {
  ApercuImport,
  ChampFicheContactModifiable,
  ContactExtrait,
  ContactResume,
  CoordonneesContact,
  EntrepriseResume,
  FicheContact,
  Membre,
  MessageChat,
  Notification,
  ReponseChat,
  ReponseCreationContact,
  ReponseNotifications,
  ReponsePropositionAction,
  ResultatExtraction,
  ResultatImport,
  TagDisponible,
} from '@ocerm/shared';
import { jetonValide, rafraichir } from './auth.ts';
import { idAppareil } from './identite.ts';
import { uuid } from './uuid.ts';
import { type ContactPicke, contactsVersVcf } from './vcf-client.ts';

export type {
  ApercuImport,
  ChampFicheContactModifiable,
  ContactResume,
  CoordonneesContact,
  EntrepriseResume,
  FicheContact,
  Membre,
  MessageChat,
  Notification,
  ReponseChat,
  ReponseCreationContact,
  ReponseNotifications,
  ReponsePropositionAction,
  ResultatExtraction,
  ResultatImport,
  TagDisponible,
};

const BASE = import.meta.env.VITE_API_URL ?? '';
// Version du build envoyée à l'API à chaque requête : le journal sait ainsi de
// quelle version vient un message/feedback (« v{semver} {sha} »).
const VERSION_CLIENT = `v${__APP_VERSION__} ${__BUILD_ID__}`;

/** Erreur d'API (#17) : porte le message RÉEL renvoyé par le serveur (plus un
 *  « Erreur 500 » opaque) et le statut HTTP. `statut` absent = pas de réponse
 *  serveur (hors-ligne / réseau) : c'est ce qui distingue « à réessayer plus tard »
 *  d'une erreur métier (400/422/403) qu'il ne faut PAS rejouer (cf. file #14). */
export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut?: number,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

async function json<T>(chemin: string, init: RequestInit): Promise<T> {
  const idUser = idAppareil();
  const faire = (jeton: string | null): Promise<Response> => {
    const enTetes: Record<string, string> = {
      ...(init.headers as Record<string, string> | undefined),
      'X-Ocerm-Version': VERSION_CLIENT,
      // Identité d'appareil -> propriété des contacts + auteur des notes (côté serveur).
      ...(idUser ? { 'X-Ocerm-User': idUser } : {}),
      ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}),
    };
    return fetch(`${BASE}${chemin}`, { ...init, headers: enTetes });
  };
  let res: Response;
  try {
    res = await faire(await jetonValide());
    // 401 sur une LECTURE (GET) : un refresh + un SEUL rejeu (pas de boucle). On NE
    // rejoue PAS les écritures (POST /action, /contacts...) : si le serveur a exécuté
    // l'action puis renvoyé 401, un rejeu doublerait la création. jetonValide()
    // rafraîchit déjà en amont, donc l'expiration en plein vol est rare.
    const methode = (init.method ?? 'GET').toUpperCase();
    if (res.status === 401 && methode === 'GET' && (await rafraichir()) === 'ok') {
      res = await faire(await jetonValide());
    }
  } catch {
    // Pas de réponse du serveur (hors-ligne, DNS, CORS) : statut absent.
    throw new ErreurApi('Connexion au serveur impossible.', undefined);
  }
  if (!res.ok) {
    // On propage le message d'erreur RÉEL du serveur quand il existe ({error}),
    // au lieu d'un statut nu (l'API renvoie déjà des messages métier lisibles).
    let message = `Erreur ${res.status}`;
    try {
      const corps = (await res.json()) as { error?: string };
      if (typeof corps?.error === 'string' && corps.error) message = corps.error;
    } catch {
      /* corps non-JSON : on garde le statut */
    }
    throw new ErreurApi(message, res.status);
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new ErreurApi('Réponse du serveur illisible.', res.status);
  }
}

/** Étape 1 du scan : analyser une photo (AUCUNE écriture). Renvoie un contact si
 *  des coordonnées sont présentes, sinon une description de l'image. */
export async function extraireCarte(photo: Blob): Promise<ResultatExtraction> {
  const form = new FormData();
  form.append('photo', photo, 'carte.jpg');
  return json<ResultatExtraction>('/extract', { method: 'POST', body: form });
}

/** Étape 2 du scan (W4) : après vérification/correction des champs par l'utilisateur,
 *  /contacts renvoie soit une alerte « doublon possible » (#9), soit une PROPOSITION
 *  signée (jeton). Rien n'est créé ici : la création passe par /action (jeton signé,
 *  un seul chemin d'écriture). `forcer` = « créer quand même » (passe outre #9). */
export async function proposerFiche(
  contact: ContactExtrait,
  forcer = false,
): Promise<ReponseCreationContact> {
  return json<ReponseCreationContact>('/contacts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(forcer ? { ...contact, forcerCreation: true } : contact),
  });
}

/** Identifiant de conversation, stable le temps de la session (regroupe les
 *  tours côté serveur pour l'analyse). Régénéré à chaque chargement de l'app. */
const conversationId = uuid();

/** Assistant conversationnel. */
export async function envoyerChat(messages: MessageChat[]): Promise<ReponseChat> {
  return json<ReponseChat>('/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, conversationId }),
  });
}

/** Commande slash « / » : mentionner des contacts du carnet pour leur ajouter une
 *  note (ou mettre à jour un champ). Renvoie une PROPOSITION signée (jeton), confirmée
 *  via /action. Aucune écriture ici. */
export async function proposerNoteMention(
  contactIds: string[],
  texte: string,
): Promise<{ description: string; jeton: string; texte?: string }> {
  return json('/note-mention', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contactIds, texte }),
  });
}

/** Exécute une écriture proposée, après confirmation de l'utilisateur.
 *  N'envoie que le jeton signé : le serveur ré-exécute l'action qu'il a signée.
 *  `resultat` porte la valeur de retour de l'outil (ex. la Fiche pour enregistrer_carte). */
export async function executerAction(
  jeton: string,
  options: { noteComplementaire?: string } = {},
): Promise<{ ok: boolean; resultat?: unknown; contactId?: string }> {
  const note = options.noteComplementaire?.trim();
  return json('/action', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(note ? { jeton, noteComplementaire: note } : { jeton }),
  });
}

/** Fiche détaillée d'un contact (données + notes) pour l'affichage et les actions. */
export async function lireContact(id: string): Promise<FicheContact> {
  return json<FicheContact>(`/contact/${id}`, { method: 'GET' });
}

/** Propose une mise à jour inline d'un champ de fiche. Aucune écriture ici : le
 *  serveur renvoie un jeton signé, confirmé ensuite via executerAction (/action). */
export async function proposerMajChampContact(
  id: string,
  champ: ChampFicheContactModifiable,
  valeur: string,
): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/contact/${encodeURIComponent(id)}/champ`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ champ, valeur }),
  });
}

/** Recherche instantanée de contacts (barre de recherche). */
export async function rechercher(
  q: string,
  options: { rapide?: boolean } = {},
): Promise<ContactResume[]> {
  const params = new URLSearchParams({ q });
  if (options.rapide) params.set('rapide', '1');
  const { resultats } = await json<{ resultats: ContactResume[] }>(`/search?${params.toString()}`, {
    method: 'GET',
  });
  return resultats;
}

/** Membres du workspace OcéRM (utilisateurs) : autocomplétion de la mention @. */
export async function listerMembres(): Promise<Membre[]> {
  const { membres } = await json<{ membres: Membre[] }>('/membres', { method: 'GET' });
  return membres;
}

/** Notes de fiche (objets datés séparés) : ajouter / modifier / supprimer. Chaque appel
 *  renvoie une PROPOSITION signée (jeton), confirmée via executerAction. */
export async function proposerAjoutNote(
  contactId: string,
  texte: string,
): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/contact/${encodeURIComponent(contactId)}/note`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ texte }),
  });
}

export async function proposerModifNote(
  noteId: string,
  texte: string,
): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/note/${encodeURIComponent(noteId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ texte }),
  });
}

export async function proposerSuppressionNote(noteId: string): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/note/${encodeURIComponent(noteId)}/supprimer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Supprime une fiche contact : proposition signée uniquement, confirmée via /action. */
export async function proposerSuppressionContact(
  contactId: string,
): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/contact/${encodeURIComponent(contactId)}/supprimer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Rattache une note orpheline à un contact (signé -> /action). */
export async function proposerRattachementNote(
  noteId: string,
  personId: string,
): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/note/${encodeURIComponent(noteId)}/rattacher`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personId }),
  });
}

/** Tags de fiche : liste de référence (picker) + poser / retirer un tag (signé). */
export async function listerTagsDispo(): Promise<TagDisponible[]> {
  const { tags } = await json<{ tags: TagDisponible[] }>('/tags', { method: 'GET' });
  return tags;
}

export async function proposerAjoutTag(
  contactId: string,
  tag: string,
): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/contact/${encodeURIComponent(contactId)}/tag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag }),
  });
}

export async function proposerRetraitTag(
  contactId: string,
  tag: string,
): Promise<ReponsePropositionAction> {
  return json<ReponsePropositionAction>(`/contact/${encodeURIComponent(contactId)}/tag/supprimer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag }),
  });
}

/** Envoie un push de test ciblé sur CET appareil (via son endpoint). `surCetAppareil`
 *  dit si l'endpoint est bien abonné sous mon membre ; `envoyees` = livré (0/1). Isole
 *  « cet appareil est-il enregistré + joignable » de « l'OS l'affiche-t-il ». */
export async function testerPushMoi(
  endpoint?: string | null,
): Promise<{ envoyees: number; surCetAppareil?: boolean }> {
  return json<{ envoyees: number; surCetAppareil?: boolean }>('/push/test-moi', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(endpoint ? { endpoint } : {}),
  });
}

/** Centre de notifications in-app : liste paginée, compteur, marquer lu. */
export async function listerNotifications(
  limit = 20,
  avant?: string | null,
): Promise<ReponseNotifications> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (avant) params.set('avant', avant);
  return json<ReponseNotifications>(`/notifications?${params.toString()}`, { method: 'GET' });
}

export async function compteurNotifications(): Promise<{ nonLues: number }> {
  return json<{ nonLues: number }>('/notifications/compteur', { method: 'GET' });
}

export async function marquerNotificationLue(
  id: string,
): Promise<{ ok: boolean; nonLues: number }> {
  return json<{ ok: boolean; nonLues: number }>(`/notifications/${encodeURIComponent(id)}/lu`, {
    method: 'POST',
  });
}

export async function marquerToutesNotificationsLues(): Promise<{
  ok: boolean;
  marquees: number;
  nonLues: number;
}> {
  return json<{ ok: boolean; marquees: number; nonLues: number }>('/notifications/lu-tout', {
    method: 'POST',
  });
}

/** Membre courant (qui suis-je, d'après le jeton W1). null si non résolu. Sert à
 *  afficher le prénom de l'utilisateur connecté dans le header. */
export async function moi(): Promise<Membre | null> {
  const { membre } = await json<{ membre: Membre | null }>('/me', { method: 'GET' });
  return membre;
}

/** Notifie par push les utilisateurs OcéRM mentionnés (@) : l'auteur est résolu serveur. */
export async function envoyerMention(
  membreIds: string[],
  texte: string,
): Promise<{ notifies: number }> {
  return json('/mention', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ membreIds, texte }),
  });
}

/** Nom de fichier déduit du type MIME réel du mémo. iPhone/Safari enregistre en
 *  mp4/AAC, pas en webm : envoyer « audio.webm » sur du mp4 fait échouer la
 *  transcription (texte vide -> « rien entendu »). */
function nomAudio(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('mp4') || t.includes('m4a') || t.includes('aac')) return 'audio.mp4';
  if (t.includes('ogg')) return 'audio.ogg';
  if (t.includes('wav')) return 'audio.wav';
  if (t.includes('mpeg') || t.includes('mp3') || t.includes('mpga')) return 'audio.mp3';
  return 'audio.webm';
}

/** Transcription d'un mémo vocal. */
export async function transcrire(audio: Blob): Promise<string> {
  const form = new FormData();
  form.append('audio', audio, nomAudio(audio.type));
  const { texte } = await json<{ texte: string }>('/transcribe', { method: 'POST', body: form });
  return texte;
}

/** Corps multipart d'un import : le .vcf (texte) + la sélection éventuelle (indices
 *  de l'aperçu à importer). Aperçu et confirmation portent le MÊME texte -> les
 *  indices sont stables (parse déterministe). */
function formVcf(texte: string, selection?: number[]): FormData {
  const form = new FormData();
  form.append('fichier', new Blob([texte], { type: 'text/vcard' }), 'contacts.vcf');
  if (selection) form.append('selection', JSON.stringify(selection));
  return form;
}

/** Aperçu (LECTURE SEULE) d'un .vcf : statut + diff par contact, rien n'est écrit. */
export async function apercuImportVcard(texte: string): Promise<ApercuImport> {
  return json<ApercuImport>('/import/vcard', { method: 'POST', body: formVcf(texte) });
}

/** Confirme l'import des contacts SÉLECTIONNÉS (indices de l'aperçu). */
export async function confirmerImportVcard(
  texte: string,
  selection: number[],
): Promise<ResultatImport> {
  return json<ResultatImport>('/import/vcard?confirmer=1', {
    method: 'POST',
    body: formVcf(texte, selection),
  });
}

/** Le Contact Picker natif n'existe que sur Chrome Android (iOS Safari ne l'expose
 *  pas). Source UNIQUE de la décision « picker vs upload .vcf » dans l'UI (§7). */
export function pickerContactsDispo(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'contacts' in navigator &&
    typeof window !== 'undefined' &&
    'ContactsManager' in window
  );
}

/** Ouvre le sélecteur de contacts natif (Android), renvoie le carnet choisi
 *  sérialisé en .vcf pour passer par le MÊME chemin d'import que l'upload (un seul
 *  parseur/dédup serveur, §5). null si l'utilisateur n'a rien choisi. */
export async function choisirContactsTelephone(): Promise<string | null> {
  const nav = navigator as Navigator & {
    contacts?: { select(props: string[], opts: { multiple: boolean }): Promise<ContactPicke[]> };
  };
  if (!nav.contacts) return null;
  const choisis = await nav.contacts.select(['name', 'tel', 'email'], { multiple: true });
  return choisis.length ? contactsVersVcf(choisis) : null;
}
